import { Component, Element, h, Host, Method, Prop } from '@stencil/core';
import HpFormField from '../_form-kit/_interfaces/HpFormField';
import { ValidatorFunction } from '../_form-kit/inline-validator/_interfaces/ValidatorFunction';
import defaultValidator from '../_form-kit/inline-validator/_default-validator';
import warn from '../../../capabilities/warn';
import uid from '../../../capabilities/identity/uid';

/**
 * @description The hp-select creates a drop-down list with themed styling.
 * @use
 * @version 4
 * @since 5.31.4
 * @slot - `default slot`: The select element to be decorated.
 */
@Component({
  tag: 'hp6-select',
  shadow: false, // NOTE: top-level form components must be shadow: false so that the label can be associated to the slotted form control.
})
export class Select implements HpFormField {
  /**
   * The help text to display.
   *
   * @see helpText
   */
  @Prop() helpText: string;

  /**
   * The height of select box to display. Sizes are defined in hp-themes
   *
   * @see InputDecorator
   */
  @Prop() heightSelection: 'md' | 'sm' = 'md';

  /**
   * The text of the label for this component. REQUIRED - Renders null if not supplied.
   *
   * @see LabelDecorator
   */
  @Prop() labelText!: string;

  /**
   * Alignment of the label, validation-message and help text
   *
   * @see LabelDecorator
   */
  @Prop() labelAlignmentSelection: 'left' | 'center' = 'left';

  /**
   * Size of label text
   *
   * @see LabelDecorator
   */
  @Prop() labelSizeSelection: 'xs' | 'sm' | 'md' | 'lg' = 'xs';

  /**
   * Type of label text
   *
   * @see LabelDecorator
   */
  @Prop() labelTypeSelection: 'heading' | 'body' = 'heading';

  /**
   * Show the <hp6-field-optional /> optional text in the label
   *
   * @see LabelDecorator
   */
  @Prop() labelShowFieldOptional: boolean = true;

  /**
   * The text to display in the <hp6-field-optional />
   */
  @Prop() labelFieldOptionalText: string;

  /**
   * If the <select /> in this slot is to be validated onBlur
   *
   * @see InlineValidator
   */
  @Prop() validateOnBlur: boolean = true;

  /**
   * If the <select /> in this slot is to be validated on load
   *
   * @see InlineValidator
   */
  @Prop() validateOnLoad: boolean = false;

  /**
   * A string attribute containing the error essage to display
   *
   * @see InlineValidator
   */
  @Prop() validatorErrorMessage: string;

  /**
   * A string attribute containing the success message to display
   *
   * @see InlineValidator
   */
  @Prop() validatorSuccessMessage: string;

  /**
   * The ValidatorFunction to use. Can only be set via Javascript.
   * Default validation returns native HTML 5 element validity
   */
  @Prop() validator: ValidatorFunction = defaultValidator;

  /**
   * Boolean that indicates whether to add an initial default option to the list of options.
   * The initial default option is not selectable. The text for this option is "— Select an option —".
   * Use the property initialDefaultOptionText to override this default text.
   */
  @Prop() addInitialDefaultOption: boolean = false;

  /**
   * String used to create an initial default option with the given text in the list of options.
   * This initial default option is not selectable. A non-blank string in this property will add
   * an initial default option to the list of options regardless of the value of the
   * addInitialDefaultOption property.
   */
  @Prop() initialDefaultOptionText: string;

  @Element() host: HTMLElement;

  get select() {
    return this.host.querySelector('select');
  }

  /**
   * Set the selectedIndex of the select
   *
   * @param selectedIndex number
   */
  @Method()
  async setSelectedIndex(selectedIndex: number = 0): Promise<void> {
    const { select } = this;
    select.selectedIndex = selectedIndex;
  }

  componentWillLoad() {
    const { select } = this;
    select.id = select.id || `select-${uid()}`;
  }

  render() {
    const {
      helpText,

      select,

      labelText,
      labelAlignmentSelection,
      labelSizeSelection,
      labelShowFieldOptional,
      labelFieldOptionalText,
      labelTypeSelection,

      validatorErrorMessage,
      validatorSuccessMessage,

      heightSelection,

      validateOnBlur,
      validateOnLoad,
      validator,

      addInitialDefaultOption,
      initialDefaultOptionText,
    } = this;

    if (!select) {
      warn(`hp-select render() select not found in slot`);
      return null;
    }

    if (!labelText) {
      warn(`hp-select render() ${select.id}: you must supply a value for the label-text prop`);
      return null;
    }

    return (
      <Host>
        {labelText
          ? (
              <hp6-label-decorator
                size-selection={labelSizeSelection}
                type-selection={labelTypeSelection}
                alignment-selection={labelAlignmentSelection}
                showFieldOptional={labelShowFieldOptional}
                fieldOptionalText={labelFieldOptionalText}
              >
                <label htmlFor={select.id}>{labelText}</label>
              </hp6-label-decorator>
            )
          : null}
        <hp6-inline-validator
          error-message={validatorErrorMessage}
          success-message={validatorSuccessMessage}
          alignment-selection={labelAlignmentSelection}
          validate-on-blur={validateOnBlur}
          validate-on-load={validateOnLoad}
          validator={validator}
        >
          <hp6-select-decorator
            height-selection={heightSelection}
            addInitialDefaultOption={addInitialDefaultOption}
            initialDefaultOptionText={initialDefaultOptionText}
          >
            <slot />
          </hp6-select-decorator>
        </hp6-inline-validator>

        {helpText && (
          <hp6-help-text
            htmlFor={select.id}
            alignment-selection={labelAlignmentSelection}
            help-text={helpText}
          />
        )}
      </Host>
    );
  }
}
